import {
  Dialog,
  DialogContent,
  Grid,
  DialogActions,
  Stack,
  Button,
} from "@mui/material";
import {
  ChoicePropertyDescriptor,
  defaultPropertyDescriptor,
  FormulaPropertyDescriptor,
  KeyPropertyDescriptor,
  LookupPropertyDescriptor,
  PropertyDescriptor,
  PropertyDescriptorType,
  PropertyDescriptorTypes,
} from "datasheets/model/core/Workbook";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { DropdownButton } from "./DropDownButton";
import {
  columnBeingEditedAtom,
  columnMappingHandlerAtom,
} from "./PropertyDescriptorAtoms";
import { Cancel } from "@mui/icons-material";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import { ColumnSelector } from "datasheets/components/selectors/ColumnSelector";
import { currentDatasetIdAtom } from "./state/datasetAtom";

export function PropertyDescriptorDialog() {
  const [columnBeingEdited, setColumnBeingEdited] = useAtom(
    columnBeingEditedAtom
  );
  const modalIsOpen = columnBeingEdited !== null;
  const closeModal = () => setColumnBeingEdited(null);
  const columnName = columnBeingEdited?.columnName ?? "";
  const [descriptorsUsingNames, setFromDescriptorUsingNames] = useAtom(
    columnMappingHandlerAtom
  );
  const handler = (newName: string, newDescriptor: PropertyDescriptor) => {
    setFromDescriptorUsingNames({
      columnId: columnBeingEdited?.columnId ?? -1,
      newName: newName,
      newDescriptor: newDescriptor,
    });
  };
  const columnDescriptor =
    columnBeingEdited && columnBeingEdited?.columnId in descriptorsUsingNames
      ? descriptorsUsingNames[columnBeingEdited.columnId]
      : defaultPropertyDescriptor();

  const [tempColumnName, setTempColumnName] = useState(columnName);
  const [tempColumnType, setTempColumnType] = useState(columnDescriptor.type);
  const [tempColumnDescriptor, setTempColumnDescriptor] =
    useState(columnDescriptor);

  // Update form values on every re-render
  useEffect(() => {
    setTempColumnName(columnName);
    setTempColumnType(columnDescriptor.type);
    setTempColumnDescriptor(columnDescriptor);
  }, [modalIsOpen]); //refreshing on the following causes issues [modalIsOpen, column, columnDescriptor]);

  function handleSubmitColumnChanges(e: React.MouseEvent<HTMLElement>) {
    handler(tempColumnName, tempColumnDescriptor);
    closeModal();
  }

  function updateTempColumnName(event: any) {
    setTempColumnName(event.target.value);
  }

  function updateTempColumnType(eventKey: any) {
    setTempColumnType(eventKey as PropertyDescriptorType);
    setTempColumnDescriptor({ ...tempColumnDescriptor, ...{ type: eventKey } });
  }

  function handleCloseDialog() {
    closeModal();
  }

  let typeParameters;
  switch (tempColumnType) {
    case PropertyDescriptorType.Formula:
      typeParameters = (
        <FormulaParameters
          columnDescriptor={tempColumnDescriptor as FormulaPropertyDescriptor}
          setColumnDescriptor={setTempColumnDescriptor}
        />
      );
      break;
    case PropertyDescriptorType.Lookup:
      typeParameters = (
        <LookupParameters
          columnDescriptor={tempColumnDescriptor as LookupPropertyDescriptor}
          setColumnDescriptor={setTempColumnDescriptor}
        />
      );
      break;
    case PropertyDescriptorType.Choice:
      typeParameters = (
        <ChoiceParameters
          columnDescriptor={tempColumnDescriptor as ChoicePropertyDescriptor}
          setColumnDescriptor={setTempColumnDescriptor}
        />
      );
      break;
    case PropertyDescriptorType.Key:
      typeParameters = (
        <KeyParameters
          columnDescriptor={tempColumnDescriptor as KeyPropertyDescriptor}
          setColumnDescriptor={setTempColumnDescriptor}
        />
      );
      break;
    default:
      typeParameters = <></>;
  }

  return (
    <Dialog
      open={modalIsOpen}
      onClose={handleCloseDialog}
      aria-label="column Header Dialog"
    >
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row">
            <label htmlFor="columnName">Column Name:</label>
            &nbsp;
            <input
              type="text"
              id="columnName"
              value={tempColumnName}
              onChange={updateTempColumnName}
            />
          </Stack>
          <Stack direction="row">
            <label htmlFor="columnType">Column Type:</label>
            &nbsp;
            <DropdownButton
              id="columnType"
              title={PropertyDescriptorType[tempColumnType]}
              onSelect={updateTempColumnType}
              options={PropertyDescriptorTypes}
            />
          </Stack>
          {typeParameters}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button onClick={handleSubmitColumnChanges} variant="outlined">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FormulaParameters({
  columnDescriptor,
  setColumnDescriptor,
}: {
  columnDescriptor: FormulaPropertyDescriptor;
  setColumnDescriptor: (columnDescriptor: PropertyDescriptor) => void;
}) {
  function updateTempColumnExpression(event: any) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ expression: event.target.value },
    });
  }

  return (
    <Stack direction="row" spacing={2}>
      <label htmlFor="columnExpression">Column Expression:</label>
      &nbsp;
      <input
        type="text"
        id="columnExpression"
        value={columnDescriptor.expression}
        onChange={updateTempColumnExpression}
      />
    </Stack>
  );
}

function LookupParameters({
  columnDescriptor,
  setColumnDescriptor,
}: {
  columnDescriptor: LookupPropertyDescriptor;
  setColumnDescriptor: (columnDescriptor: PropertyDescriptor) => void;
}) {
  const datasetId = useAtomValue(currentDatasetIdAtom);
  function updateReferenceSheet(value: string) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ referenceSheet: value },
    });
  }

  function updateKeyColumnInThisSheet(value: string) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ keyColumnInCurrentSheet: value },
    });
  }

  function updateKeyColumnInReferenceSheet(value: string) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ keyColumnInReferenceSheet: value },
    });
  }

  function updateReferenceColumnInReferenceSheet(value: string) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ referenceColumnInReferenceSheet: value },
    });
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DataSetSelector
          title="Reference Sheet"
          selection={columnDescriptor.referenceSheet}
          onSelect={updateReferenceSheet}
        />
      </Grid>
      <Grid item xs={12}>
        <ColumnSelector
          title="Key Column In This Sheet"
          datasetId={datasetId}
          selection={columnDescriptor.keyColumnInCurrentSheet}
          onChange={updateKeyColumnInThisSheet}
          isSingleSelection={true}
        />
      </Grid>
      <Grid item xs={12}>
        <ColumnSelector
          title="Key Column In Reference Sheet"
          datasetId={columnDescriptor.referenceSheet}
          selection={columnDescriptor.keyColumnInReferenceSheet}
          onChange={updateKeyColumnInReferenceSheet}
          isSingleSelection={true}
        />
      </Grid>
      <Grid item xs={12}>
        <ColumnSelector
          title="Reference Column In Reference Sheet"
          datasetId={columnDescriptor.referenceSheet}
          selection={columnDescriptor.referenceColumnInReferenceSheet}
          onChange={updateReferenceColumnInReferenceSheet}
          isSingleSelection={true}
        />
      </Grid>
    </Grid>
  );
}

function ChoiceParameters({
  columnDescriptor,
  setColumnDescriptor,
}: {
  columnDescriptor: ChoicePropertyDescriptor;
  setColumnDescriptor: (columnDescriptor: PropertyDescriptor) => void;
}) {
  const [newOption, setNewOption] = useState("");
  function addOption() {
    const newOptions = !!columnDescriptor.options
      ? [...columnDescriptor.options]
      : [];
    newOptions.push(newOption);
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ options: newOptions },
    });
    setNewOption("");
  }
  function updateOption(idx: number, value: string) {
    const newOptions = [...columnDescriptor.options];
    newOptions[idx] = value;
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ options: newOptions },
    });
  }
  function removeOption(idx: number) {
    const newOptions = columnDescriptor.options.splice(idx, 1);
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ options: newOptions },
    });
  }

  const options = !!columnDescriptor.options
    ? columnDescriptor.options.map((option, idx) => {
        return (
          <div key={"option" + idx}>
            <input
              type="text"
              id={"option" + idx}
              value={option}
              onChange={(event) => updateOption(idx, event.target.value)}
            />
            <Cancel onClick={() => removeOption(idx)} />
          </div>
        );
      })
    : [];

  return (
    <Stack direction="column" spacing={2}>
      <label>Options:</label>
      {options}
      <input
        type="text"
        id="newOption"
        value={newOption}
        onChange={(event) => setNewOption(event.target.value)}
        onBlur={() => addOption()}
      />
    </Stack>
  );
}

function KeyParameters({
  columnDescriptor,
  setColumnDescriptor,
}: {
  columnDescriptor: KeyPropertyDescriptor;
  setColumnDescriptor: (columnDescriptor: PropertyDescriptor) => void;
}) {
  function updateReferenceSheet(value: any) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ referenceSheet: value },
    });
  }
  function updateOptionsColumn(value: any) {
    setColumnDescriptor({
      ...columnDescriptor,
      ...{ optionsColumn: value },
    });
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DataSetSelector
          title="Reference Sheet"
          selection={columnDescriptor.referenceSheet}
          onSelect={updateReferenceSheet}
        />
      </Grid>
      <Grid item xs={12}>
        <ColumnSelector
          title="Reference Column In Reference Sheet"
          datasetId={columnDescriptor.referenceSheet}
          selection={columnDescriptor.optionsColumn}
          onChange={updateOptionsColumn}
          isSingleSelection={true}
        />
      </Grid>
    </Grid>
  );
}
